import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "components/ui/tooltip";
import SvgIcon from "components/base/SvgIcon";
import api from "apiSingleton";
import { JOB_APPLICATION_MATCH_STATUS } from "lookup";
import { copyToClipboard } from "utils/copyToClipboard";
import { cn } from "utils";

const COLUMN_TITLE_CLASSNAME = "font-inter text-sm font-medium text-slate-700";
const BULLET_ITEM_CLASSNAME =
  "font-inter text-sm font-normal text-slate-700 underline decoration-dotted underline-offset-[25%] decoration-[11%] decoration-[#0166FF]";

const useUserRecentNotes = (userId, shouldFetch) => {
  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState([]);

  const getUserNotes = useCallback(async () => {
    let allNotes = [];
    let nextToken = null;

    setLoading(true);

    try {
      do {
        const response = await api.notes.listUser({ nextToken, userId });
        const data = response?.data?.listUserNotes;

        if (data?.items) {
          allNotes = [...allNotes, ...data.items];
        }

        nextToken = data?.nextToken;
      } while (nextToken);

      allNotes.sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
      setNotes(allNotes.slice(0, 3));
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (shouldFetch && userId) {
      getUserNotes();
    }
  }, [shouldFetch, userId, getUserNotes]);

  return { loading, notes };
};

const useUserRecentMatches = (userId, shouldFetch) => {
  const [loading, setLoading] = useState(false);
  const [matches, setMatches] = useState([]);

  const getUserMatches = useCallback(async () => {
    setLoading(true);

    try {
      const allMatches = await api.match.getMatchesForUser({ userId });
      const threeMonthsAgo = new Date();
      threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);

      const recentMatches = allMatches
        .filter((match) => {
          const matchedDate = new Date(match.createdAt);

          return (
            match.jobOpportunity &&
            match.status === JOB_APPLICATION_MATCH_STATUS.MATCHED &&
            matchedDate >= threeMonthsAgo
          );
        })
        .slice(0, 3);

      setMatches(recentMatches);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (shouldFetch && userId) {
      getUserMatches();
    }
  }, [shouldFetch, userId, getUserMatches]);

  return { loading, matches };
};

const ContextColumn = ({ title, children }) => (
  <div className="min-w-0 flex-1">
    <h4 className={COLUMN_TITLE_CLASSNAME}>{title}</h4>
    <ul className="mt-2 space-y-1.5 [&>li]:relative [&>li]:pl-3 [&>li]:text-sm [&>li]:leading-5 [&>li]:before:absolute [&>li]:before:left-0 [&>li]:before:top-[calc((1lh-2px)/2)] [&>li]:before:h-[2px] [&>li]:before:w-[2px] [&>li]:before:rounded-full [&>li]:before:bg-slate-700">
      {children}
    </ul>
  </div>
);

const ContactField = ({ icon, value, label, copyTitle }) => {
  if (!value) {
    return null;
  }

  return (
    <div className="flex min-w-0 items-center gap-1.5">
      <SvgIcon
        type={icon}
        className="h-[15px] w-[15px] shrink-0 text-slate-700"
      />
      <span className={cn(BULLET_ITEM_CLASSNAME, "truncate")}>{value}</span>
      <button
        type="button"
        className="inline-flex shrink-0 items-center justify-center text-gray-500 transition hover:text-slate-700"
        title={copyTitle}
        aria-label={copyTitle}
        onClick={() => copyToClipboard(value, label)}
      >
        <SvgIcon type="copy" className="h-[17px] w-[15px]" />
      </button>
    </div>
  );
};

const UserContact = ({ user }) => {
  const email = user.email;
  const phoneNumber = user.phone?.number;

  if (!email && !phoneNumber) {
    return null;
  }

  return (
    <div>
      <h4 className={COLUMN_TITLE_CLASSNAME}>Contact</h4>
      <div className="mt-2 flex flex-wrap items-center gap-x-6 gap-y-2">
        <ContactField
          icon="atSign"
          value={email}
          label="Email"
          copyTitle="Copy email"
        />
        <ContactField
          icon="phoneHandset"
          value={phoneNumber}
          label="Phone number"
          copyTitle="Copy phone number"
        />
      </div>
    </div>
  );
};

const UserContextColumns = ({ user, shouldFetch }) => {
  const { loading: notesLoading, notes } = useUserRecentNotes(
    user.id,
    shouldFetch
  );
  const { loading: matchesLoading, matches } = useUserRecentMatches(
    user.id,
    shouldFetch
  );

  const careerTitles = useMemo(
    () =>
      [
        ...new Set(
          (user.careers ?? [])
            .filter((career) => career?.title)
            .map((career) =>
              career.companyName
                ? `${career.title} @ ${career.companyName}`
                : career.title
            )
        ),
      ]
        .filter(Boolean)
        .slice(0, 3),
    [user.careers]
  );

  const showNotes = !notesLoading && notes.length > 0;
  const showMatches = !matchesLoading && matches.length > 0;
  const showCareers = careerTitles.length > 0;
  const hasColumns = showNotes || showMatches || showCareers;
  const hasContact = Boolean(user.email || user.phone?.number);

  if (!hasColumns && !hasContact) {
    if (notesLoading || matchesLoading) {
      return (
        <>
          <div className="border-t border-slate-100" />
          <p className="font-inter text-sm font-normal text-slate-500">
            Loading user details...
          </p>
        </>
      );
    }

    return null;
  }

  return (
    <>
      <div className="border-t border-slate-100" />

      <div className="flex flex-col gap-4">
        {hasColumns ? (
          <div className="flex flex-col gap-6 sm:flex-row sm:gap-8">
            {showNotes ? (
              <ContextColumn title="User notes">
                {notes.map((note) => (
                  <li key={note.id}>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span
                            className={cn(
                              BULLET_ITEM_CLASSNAME,
                              "cursor-help text-left"
                            )}
                          >
                            {note.content?.length > 30
                              ? `${note.content.slice(0, 30)}...`
                              : note.content}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-sm">
                          <p>{note.content}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </li>
                ))}
              </ContextColumn>
            ) : null}

            {showMatches ? (
              <ContextColumn title="Recent Matches">
                {matches.map((match) => (
                  <li key={match.jobOpportunity.id}>
                    <a
                      className={BULLET_ITEM_CLASSNAME}
                      href={`/#/jobOpps/${match.jobOpportunity.id}/details`}
                      rel="noreferrer"
                      target="_blank"
                    >
                      {match.jobOpportunity.title}
                    </a>
                  </li>
                ))}
              </ContextColumn>
            ) : null}

            {showCareers ? (
              <ContextColumn title="Recent Job titles">
                {careerTitles.map((title) => (
                  <li key={title}>
                    <span className={BULLET_ITEM_CLASSNAME}>{title}</span>
                  </li>
                ))}
              </ContextColumn>
            ) : null}
          </div>
        ) : notesLoading || matchesLoading ? (
          <p className="font-inter text-sm font-normal text-slate-500">
            Loading user details...
          </p>
        ) : null}

        <UserContact user={user} />
      </div>
    </>
  );
};

export default UserContextColumns;
